import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

type Slot = {
    host: HTMLElement | null;

    // retain marks the slot as in-use and returns the release callback
    retain: () => () => void;
};

const empty: Slot = { host: null, retain: () => () => {} };

export const SidePanelContext = createContext<Slot>(empty);

// useSidePanelSlot holds the state for the right-hand panel slot. The layout that
// calls it renders the host element and provides the slot (see Layout).
export function useSidePanelSlot() {
    const [host, setHost] = useState<HTMLElement | null>(null);
    const [retained, setRetained] = useState(0);

    const slot = useMemo<Slot>(() => ({
        host: host,
        retain: () => {
            setRetained(count => count + 1);
            return () => setRetained(count => count - 1);
        },
    }), [host]);

    return { slot: slot, setHost: setHost, open: retained > 0 };
}

// SidePanel puts its children in the layout's right-hand slot, which sits beside
// the page content and runs from the header bar to the bottom of the window.
export function SidePanel(props: PropsWithChildren<{}>) {
    const slot = useContext(SidePanelContext);
    const retain = slot.retain;

    useEffect(() => retain(), [retain]);

    if (!slot.host) return null;
    return createPortal(props.children, slot.host);
}
